import { ClientType, Gender } from '@massilia/prisma-generated/enums';
import { z } from 'zod';
import { emptyStringToNull, pageParam, pageSizeParam } from '../_shared/coerce.js';
import { isoDateString } from '../_shared/dates.js';
import { personName } from '../_shared/person-name.js';
import { validatePhoneNumber } from '../helpers/country-calling-codes.js';
import { createPhoneNumberSchema } from './contact.schema.js';
// Numéro avec validation de format (libphonenumber) en plus du type — source
// unique consommée côté serveur et côté formulaire backoffice.
const createClientPhoneSchema = createPhoneNumberSchema.extend({
    number: z
        .string()
        .min(1, 'Le numéro de téléphone est requis.')
        .refine((value) => validatePhoneNumber(value) === undefined, 'Numéro de téléphone invalide.'),
});
const phoneNumbersField = z
    .array(createClientPhoneSchema)
    .min(1, 'Au moins un numéro de téléphone est requis.');
/**
 * Création de l'espace client par l'admin : soit l'admin saisit directement un
 * mot de passe, soit on envoie une invitation par email (le client définit son
 * mot de passe via un lien valable 7 jours).
 *
 * Défini ici (avant les schémas de création) car il est embarqué de façon
 * optionnelle dans `createClientSchema` pour créer client + espace client en un
 * seul appel atomique.
 */
export const adminCreateClientAccountSchema = z
    .discriminatedUnion('mode', [
    z.object({
        mode: z.literal('password'),
        password: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères.'),
        passwordConfirmation: z.string().min(8),
    }),
    z.object({
        mode: z.literal('invitation'),
    }),
])
    .refine((data) => data.mode !== 'password' || data.password === data.passwordConfirmation, {
    message: 'Les mots de passe ne correspondent pas.',
    path: ['passwordConfirmation'],
});
/** Particulier : prénom / nom obligatoires, le reste optionnel. */
export const createIndividualClientSchema = z.object({
    email: emptyStringToNull(z.string().email()),
    firstName: personName(),
    lastName: personName(),
    secondaryEmail: emptyStringToNull(z.string().email()),
    account: adminCreateClientAccountSchema.optional(),
    address: emptyStringToNull(z.string()),
    birthDate: emptyStringToNull(isoDateString()),
    city: emptyStringToNull(z.string()),
    clientType: z.literal(ClientType.INDIVIDUAL),
    country: emptyStringToNull(z.string()),
    gender: z.nativeEnum(Gender).optional(),
    note: emptyStringToNull(z.string()),
    phoneNumbers: phoneNumbersField,
    postalCode: emptyStringToNull(z.string()),
});
/**
 * B2B : raison sociale, prénom/nom du contact référent, email, téléphone et
 * adresse complète (rue + code postal + ville) obligatoires ; SIRET/SIREN et
 * notes facultatifs. Le contact référent alimente `Client.firstName`/`lastName`
 * (utilisés notamment par la sync IGA).
 */
export const createCompanyClientSchema = z.object({
    companyName: z.string().trim().min(1, 'Le nom est requis.'),
    email: z.string().min(1, "L'adresse email est requise.").email('Adresse email invalide.'),
    firstName: personName(),
    lastName: personName(),
    account: adminCreateClientAccountSchema.optional(),
    address: z.string().trim().min(1, "L'adresse est requise."),
    city: z.string().trim().min(1, 'La ville est requise.'),
    clientType: z.literal(ClientType.COMPANY),
    country: emptyStringToNull(z.string()),
    note: emptyStringToNull(z.string()),
    phoneNumbers: phoneNumbersField,
    postalCode: z.string().trim().min(1, 'Le code postal est requis.'),
    siret: emptyStringToNull(z.string()),
});
export const createClientSchema = z.discriminatedUnion('clientType', [
    createIndividualClientSchema,
    createCompanyClientSchema,
]);
export const updateClientSchema = z.object({
    companyName: emptyStringToNull(z.string()),
    email: emptyStringToNull(z.string().email()),
    firstName: personName({ optional: true }),
    lastName: personName({ optional: true }),
    secondaryEmail: emptyStringToNull(z.string().email()),
    address: emptyStringToNull(z.string()),
    birthDate: emptyStringToNull(isoDateString()),
    city: emptyStringToNull(z.string()),
    country: emptyStringToNull(z.string()),
    gender: z.nativeEnum(Gender).optional(),
    note: emptyStringToNull(z.string()),
    postalCode: emptyStringToNull(z.string()),
    siret: emptyStringToNull(z.string()),
});
export const adminUpdateClientEmailSchema = z.object({
    email: z
        .string()
        .min(1, "L'adresse email est requise.")
        .email('Veuillez saisir une adresse email valide.'),
});
export const adminUpdateClientPasswordSchema = z.object({
    password: z.string().min(8),
    passwordConfirmation: z.string().min(8),
});
export const getClientsQuerySchema = z.object({
    clientType: z.nativeEnum(ClientType).optional(),
    page: pageParam(),
    pageSize: pageSizeParam(),
    search: z.string().optional(),
});
/** Acceptation d'une invitation espace client : le client définit son mot de passe. */
export const acceptInvitationSchema = z
    .object({
    invitationToken: z.string().min(1),
    password: z.string().min(8, 'Le mot de passe doit contenir au moins 8 caractères.'),
    passwordConfirmation: z.string().min(8),
})
    .refine((data) => data.password === data.passwordConfirmation, {
    message: 'Les mots de passe ne correspondent pas.',
    path: ['passwordConfirmation'],
});
